import {
  Suspense,
  useMemo,
  useState,
  useCallback,
  useEffect,
  useRef,
  Component,
} from "react";
import * as THREE from "three";
import {
  OrbitControls,
  useGLTF,
  useTexture,
  Center,
  Bounds,
  useBounds,
  Environment,
  useProgress,
} from "@react-three/drei";
import { applyFinish } from "./vanityMaterials.js";
import { asset } from "../assetUrl.js";

// The app opens on 1C.U1.80, so start fetching it immediately rather than
// waiting for React to mount and the component tree to resolve.
useGLTF.preload(asset("/models/vanity/countertops/1C.U1.80.glb"));

const DEFAULT_COUNTERTOP = asset("/textures/vanity/countertop/QW.jpg");
const DEFAULT_STORAGE = asset("/textures/kitchen_cabinets/Alpine White W1100 ST9.jpg");

// Finish configs. Storage matches the kitchen's fully-matte laminate look; the
// countertop keeps a light satin sheen so it still catches a highlight.
// TEXTURE SCALE. The GLB UVs map one full texture image across exactly 3.60 m
// of real surface (measured: identical on every model, and isotropic in U and
// V). A material photo represents roughly a 1.8 m slab, so the texture must be
// repeated twice or the pattern renders at double its real-world size.
//   repeat 2 -> one texture per 1.80 m   (default)
//   repeat 3 -> one texture per 1.20 m
const W_ = typeof window !== "undefined" ? window : {};
const COUNTERTOP_REPEAT = W_.__CT_REPEAT ?? 2;
const STORAGE_REPEAT = W_.__ST_REPEAT ?? 2;

const COUNTERTOP_FINISH = {
  roughness: 0.2,
  metalness: 0.0,
  envMapIntensity: 1.0,
  repeat: [COUNTERTOP_REPEAT, COUNTERTOP_REPEAT],
};
const STORAGE_FINISH = {
  roughness: 0.5,
  metalness: 0.0,
  envMapIntensity: 1.0,
  repeat: [STORAGE_REPEAT, STORAGE_REPEAT],
};

// How far the camera sits back from the fitted model (higher = more zoomed out).
const FIT_MARGIN = 1.6;

// Ground shadow placement: dropped below the model's base by a fraction of its
// height (so it reads as a floor shadow, not a dark patch stuck to the model),
// and spread a little wider than the footprint.
const SHADOW_GAP = 1.2; // × model height
const SHADOW_GAP_MIN = 0.38; // metres — keeps thin countertops clear of the shadow
const SHADOW_GAP_MAX = 0.6; // metres — stops tall assemblies drifting too far
const SHADOW_SPREAD_X = 1.35;
const SHADOW_SPREAD_Z = 1.05;

class PartBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
  }
  static getDerivedStateFromError() {
    return { failed: true };
  }
  componentDidCatch(err) {
    console.warn("[vanity] failed to load part:", this.props.path, err?.message || err);
  }
  componentDidUpdate(prev) {
    if (prev.path !== this.props.path && this.state.failed) {
      this.setState({ failed: false });
    }
  }
  render() {
    if (this.state.failed) return null;
    return this.props.children;
  }
}

function Part({ path, texture, fallback, finishOverrides }) {
  const { scene } = useGLTF(path);
  const tex = useTexture(texture || fallback);

  const prepared = useMemo(() => {
    const s = scene.clone(true);
    applyFinish(s, tex, finishOverrides);
    return s;
  }, [scene, tex, finishOverrides]);

  return <primitive object={prepared} />;
}

// Re-frames the camera once the model has real geometry, then reports the
// measured bounds upward (used to place the ground shadow).
function FitOnLoad({ deps, onFitted }) {
  const bounds = useBounds();
  useEffect(() => {
    bounds.refresh().clip().fit();
    const t = setTimeout(() => onFitted?.(), 60);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return null;
}

// Re-frames after the shadow appears so the camera always includes it (a tall
// counter+storage assembly would otherwise push the shadow off the bottom).
function RefitWithShadow({ dims }) {
  const bounds = useBounds();
  useEffect(() => {
    if (!dims) return;
    const t = setTimeout(() => bounds.refresh().clip().fit(), 30);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dims]);
  return null;
}

// Soft ground shadow. Uses a radial-gradient texture on a plane rather than a
// render-target shadow pass: it always renders (no depth-texture support
// required), costs nothing per frame, and gives the diffuse blob the design
// calls for.
function SoftShadow({ y, width, depth }) {
  const texture = useMemo(() => {
    const size = 512; // higher res = smoother gradient, no banding
    const c = document.createElement("canvas");
    c.width = size;
    c.height = size;
    const ctx = c.getContext("2d");
    const g = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);

    // Gaussian falloff sampled over many stops. A piecewise-linear gradient
    // shows a visible crease where the segments join; this curve is continuous.
    // Normalised so alpha reaches exactly 0 at the edge (no hard rim).
    const PEAK = 0.28;
    const K = 1.9; // higher = tighter core, lower = broader haze
    const STOPS = 40;
    const tail = Math.exp(-K);
    for (let i = 0; i <= STOPS; i++) {
      const r = i / STOPS;
      const a = (PEAK * (Math.exp(-K * r * r) - tail)) / (1 - tail);
      g.addColorStop(r, `rgba(0,0,0,${Math.max(a, 0).toFixed(4)})`);
    }

    ctx.fillStyle = g;
    ctx.fillRect(0, 0, size, size);
    const t = new THREE.CanvasTexture(c);
    t.colorSpace = THREE.SRGBColorSpace;
    t.minFilter = THREE.LinearFilter;
    t.magFilter = THREE.LinearFilter;
    t.generateMipmaps = false;
    return t;
  }, []);

  useEffect(() => () => texture.dispose(), [texture]);

  return (
    <mesh position={[0, y, 0]} rotation={[-Math.PI / 2, 0, 0]} renderOrder={-1}>
      <planeGeometry args={[width, depth]} />
      <meshBasicMaterial map={texture} transparent depthWrite={false} toneMapped={false} />
    </mesh>
  );
}

// Real HDRI environment: gives varied, physically-plausible reflections on the
// glossy countertop instead of flat white panels. Bundled locally under
// /hdri/ (no external CDN), 512x256 EXR ~100KB. Swap HDRI_FILE to change mood:
// neutral (default) | apartment | warehouse | lobby | studio.
// "neutral" is a GREYSCALE environment (generated from apartment.exr): it keeps
// the luminance structure that makes reflections read, but has zero colour, so
// no surface direction can tint a finish. Photographic HDRIs have coloured
// hemispheres — apartment's warm ceiling made upward-facing countertop surfaces
// render +13 warmer than the vertical faces, which looked like a beige patch on
// a white top that did not move when the model was rotated.
// NOTE: "studio" is a near-uniform white environment — its reflections blast
// neutral white over light finishes and destroy their tint (measured: only ~1%
// of pixels kept the beige of Sanded Sahara vs ~65% with apartment/lobby).
// Prefer an interior HDRI with tonal variation so albedo still reads.
const HDRI_FILE =
  (typeof window !== "undefined" && window.__HDRI) || "photostudio_neutral";
// Environment strength. Measured sweet spot: white stays neutral and bright
// while beige keeps its tint (see RENDERING_README).
const ENV_INTENSITY =
  (typeof window !== "undefined" && window.__ENVI) ?? 0.8;

function HdriEnvironment() {
  return (
    <Environment
      files={asset(`/hdri/${HDRI_FILE}.exr`)}
      environmentIntensity={ENV_INTENSITY}
      background={false}
    />
  );
}

function Vanity({ countertopPath, storagePath, textures, onMeasured }) {
  const groupRef = useRef();

  // Measure the assembled model in WORLD space. drei's <Center onCentered>
  // doesn't report width/height/depth in this version, so compute the box here.
  const report = useCallback(() => {
    const g = groupRef.current;
    if (!g) return;
    g.updateWorldMatrix(true, true);
    const box = new THREE.Box3().setFromObject(g);
    if (box.isEmpty()) return;
    const size = new THREE.Vector3();
    box.getSize(size);
    if (![size.x, size.y, size.z].every(Number.isFinite) || size.y <= 0) return;
    onMeasured({ width: size.x, height: size.y, depth: size.z, minY: box.min.y });
  }, [onMeasured]);

  return (
    <Center onCentered={report}>
      <group ref={groupRef}>
        <PartBoundary path={countertopPath}>
          <Suspense fallback={null}>
            <Part
              path={countertopPath}
              texture={textures.countertop}
              fallback={DEFAULT_COUNTERTOP}
              finishOverrides={COUNTERTOP_FINISH}
            />
            <FitOnLoad deps={[countertopPath, storagePath]} onFitted={report} />
          </Suspense>
        </PartBoundary>

        {storagePath && (
          <PartBoundary path={storagePath}>
            <Suspense fallback={null}>
              <Part
                path={storagePath}
                texture={textures.storage}
                fallback={DEFAULT_STORAGE}
                finishOverrides={STORAGE_FINISH}
              />
              <FitOnLoad deps={[storagePath, countertopPath]} onFitted={report} />
            </Suspense>
          </PartBoundary>
        )}
      </group>
    </Center>
  );
}

export default function VanityExperience({ countertopPath, storagePath, textures }) {
  const [dims, setDims] = useState(null);

  const handleMeasured = useCallback((next) => {
    setDims((prev) => {
      if (
        prev &&
        Math.abs(prev.height - next.height) < 1e-4 &&
        Math.abs(prev.width - next.width) < 1e-4 &&
        Math.abs(prev.depth - next.depth) < 1e-4 &&
        Math.abs(prev.minY - next.minY) < 1e-4
      ) {
        return prev; // unchanged — avoid a re-render loop
      }
      return next;
    });
  }, []);

  // Clear the stale shadow while a new selection loads.
  useEffect(() => {
    if (!countertopPath) setDims(null);
  }, [countertopPath]);

  return (
    <>
      <color attach="background" args={["#f7f7f7"]} />
      <OrbitControls makeDefault />

      {/* Light rig copied from the reference configurator */}
      <ambientLight intensity={0.3} />
      <spotLight
        intensity={3.5}
        position={[3, 1, 0]}
        rotation={[0, Math.PI, Math.PI / 2]}
        scale={10}
      />

      {/* Subtle reflections for the countertop sheen */}
      <Suspense fallback={null}>
        <HdriEnvironment />
      </Suspense>

      {countertopPath && (
        <Bounds fit clip margin={FIT_MARGIN}>
          <Vanity
            countertopPath={countertopPath}
            storagePath={storagePath}
            textures={textures}
            onMeasured={handleMeasured}
          />

          {/* Soft ground shadow, dropped below the model so it reads as a floor
              shadow. Inside <Bounds> so the camera frames model + shadow. */}
          {dims && (
            <SoftShadow
              y={
                dims.minY -
                Math.min(
                  Math.max(dims.height * SHADOW_GAP, SHADOW_GAP_MIN),
                  SHADOW_GAP_MAX
                )
              }
              width={Math.max(dims.width, 0.2) * SHADOW_SPREAD_X}
              depth={Math.max(dims.depth, 0.2) * SHADOW_SPREAD_Z}
            />
          )}

          <RefitWithShadow dims={dims} />
        </Bounds>
      )}
    </>
  );
}
